import { DashboardFolderT, DashboardT } from 'business/definitions';
import { useMemo, useState, useEffect } from 'react';
import DashboardsGrid from 'routes/AllDashboards/components/DashboardsGrid/DashboardsGrid';
import { Box, useTheme } from '@mui/system';
import DashboardsTable from '../DashboardsList/DashboardsTable';
import { useGetDashboardFolders } from 'routes/AllDashboards/hooks/dashboardFolders';
import Loading from 'components/Loading';
import { filterDashboardsBySearch, updateDashboardFolders } from './helpers';
import DashboardsTabHeader from '../Header/DashboardsTabHeader';
import Flex from 'ui/Flex/Flex';
import { Button, Typography } from '@mui/material';
import { useIsEssential, useIsExploreUser, useUserHasRole } from 'hooks/client';
import { SUPPORT } from 'business/constants';
import { useTranslation } from 'react-i18next';
import CreateDashboardModal from 'components/DashboardAddForm/CreateDashboardModal';
import { useNavigate } from 'react-router-dom';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';

type Props = { dashboards: DashboardT[]; ['data-testid']?: string };

const DashboardsView = ({ dashboards, 'data-testid': dataTestId }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [searchValue, setSearchValue] = useState('');
    const isSearching = searchValue !== '';
    const [viewDisplay, setViewDisplay] = useState(
        () => localStorage.getItem('dashboardsDisplay') || 'grid',
    );
    const [currentFolder, setCurrentFolder] = useState<DashboardFolderT | null>(
        null,
    );

    const displayedDashboards = useMemo(() => {
        if (currentFolder) {
            return filterDashboardsBySearch(
                currentFolder.dashboards,
                searchValue,
            );
        }
        return filterDashboardsBySearch(dashboards, searchValue, true);
    }, [currentFolder, dashboards, searchValue]);

    const {
        data: dashboardFolders = [],
        isLoading: isDashboardFoldersLoading,
    } = useGetDashboardFolders();

    const updatedDashboardFolders = useMemo(
        () => updateDashboardFolders(dashboardFolders, dashboards),
        [dashboardFolders, dashboards],
    );

    useEffect(() => {
        if (currentFolder) {
            const matchingFolder = updatedDashboardFolders.find(
                (folder) => folder.id === currentFolder.id,
            );
            setCurrentFolder(matchingFolder ?? null);
        }
    }, [updatedDashboardFolders, currentFolder]);

    const [showNewDashboardModal, setShowNewDashboardModal] = useState(false);
    const isExploreUser = useIsExploreUser();
    const hasEssential = useIsEssential();
    const isSupportUser = useUserHasRole(SUPPORT);
    const canAddForExploreUser = !hasEssential && isExploreUser;
    const canAddForEssentialClient = hasEssential && isSupportUser;

    const newDashboardButton =
        canAddForExploreUser || canAddForEssentialClient ? (
            <Button
                variant="contained"
                startIcon={<AutoFixHighOutlinedIcon />}
                onClick={() => setShowNewDashboardModal(true)}
                data-testid="empty-create-dashboard-button"
            >
                <Typography>{t('dashboards.create')}</Typography>
            </Button>
        ) : (
            <></>
        );

    if (isDashboardFoldersLoading) return <Loading />;
    return (
        <>
            <Box data-testid={dataTestId}>
                <DashboardsTabHeader
                    setSearchValue={setSearchValue}
                    showToggleDisplayButton
                    viewDisplay={viewDisplay}
                    setViewDisplay={setViewDisplay}
                />
                {!isSearching && displayedDashboards.length === 0 ? (
                    <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        mt={theme.spacing(15)}
                        gap={1}
                    >
                        <Typography>{t('dashboards.noDashboards')}</Typography>
                        {newDashboardButton}
                    </Flex>
                ) : viewDisplay === 'grid' ? (
                    <DashboardsGrid
                        folders={updatedDashboardFolders}
                        dashboards={displayedDashboards}
                        sx={{
                            margin: theme.spacing(2),
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(2),
                        }}
                        isSearching={isSearching}
                        currentFolder={currentFolder}
                        handleFolder={setCurrentFolder}
                    />
                ) : (
                    <DashboardsTable
                        folders={updatedDashboardFolders}
                        dashboards={displayedDashboards}
                        isSearching={isSearching}
                        handleFolder={setCurrentFolder}
                        currentFolder={currentFolder}
                    />
                )}
            </Box>
            {showNewDashboardModal && (
                <CreateDashboardModal
                    onCreate={(dashboard) =>
                        navigate(`/dashboard/${dashboard.slug}`)
                    }
                    isOpen={showNewDashboardModal}
                    onClose={() => setShowNewDashboardModal(false)}
                />
            )}
        </>
    );
};

export default DashboardsView;
